import React, { useState } from 'react';
import { FileText, Sparkles, Database, Users, Lock, Globe, Check } from 'lucide-react';
import { allFeatures } from '../data/mock';

const iconMap = {
  FileText,
  Sparkles,
  Database,
  Users,
  Lock,
  Globe
};

const colorMap = {
  blue: { bg: 'bg-blue-500/20', text: 'text-blue-400', border: 'border-blue-500/30', activeBg: 'bg-blue-600' },
  violet: { bg: 'bg-violet-500/20', text: 'text-violet-400', border: 'border-violet-500/30', activeBg: 'bg-violet-600' },
  emerald: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', border: 'border-emerald-500/30', activeBg: 'bg-emerald-600' },
  amber: { bg: 'bg-amber-500/20', text: 'text-amber-400', border: 'border-amber-500/30', activeBg: 'bg-amber-600' },
  red: { bg: 'bg-red-500/20', text: 'text-red-400', border: 'border-red-500/30', activeBg: 'bg-red-600' },
  cyan: { bg: 'bg-cyan-500/20', text: 'text-cyan-400', border: 'border-cyan-500/30', activeBg: 'bg-cyan-600' }
};

const FeaturesSection = () => {
  const [activeFeature, setActiveFeature] = useState('documentManagement');
  const featureKeys = Object.keys(allFeatures);
  const activeData = allFeatures[activeFeature];
  const IconComponent = iconMap[activeData.icon];
  const colors = colorMap[activeData.color];

  return (
    <section id="features" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-1.5 rounded-full bg-blue-100 text-blue-700 text-sm font-semibold mb-4">
            Özellikler
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-800 mb-6">
            Tüm İhtiyaçlarınız,
            <span className="text-blue-600"> Tek Platform</span>
          </h2>
          <p className="max-w-2xl mx-auto text-lg text-slate-600">
            CAPSNA ile doküman yönetiminden iş zekasına, yapay zekadan güvenliğe kadar
            tüm kurumsal ihtiyaçlarınızı karşılayın.
          </p>
        </div>

        {/* Feature Tabs */}
        <div className="flex flex-wrap justify-center gap-2 md:gap-3 mb-12">
          {featureKeys.map((key) => {
            const feature = allFeatures[key];
            const Icon = iconMap[feature.icon];
            const isActive = activeFeature === key;
            const fColors = colorMap[feature.color];

            return (
              <button
                key={key}
                onClick={() => setActiveFeature(key)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-300 ${
                  isActive
                    ? `${fColors.activeBg} text-white shadow-lg`
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                <Icon size={18} />
                <span className="hidden sm:inline">{feature.title}</span>
              </button>
            );
          })}
        </div>

        {/* Active Feature Content */}
        <div className="bg-slate-50 rounded-3xl border border-slate-200 overflow-hidden">
          <div className="grid md:grid-cols-5 gap-0">
            {/* Left - Info */}
            <div className={`md:col-span-2 p-8 md:p-12 ${colors.activeBg}`}>
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center">
                  <IconComponent className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-2xl md:text-3xl font-bold text-white">
                  {activeData.title}
                </h3>
              </div>
              <p className="text-white/80 text-lg mb-6">
                CAPSNA'nın {activeData.title.toLowerCase()} modülü ile kurumsal verimliliğinizi artırın.
              </p>
              <div className="flex items-center gap-2 text-white/70">
                <span className="text-sm">{activeData.items.length} özellik mevcut</span>
              </div>
            </div>

            {/* Right - Feature List */}
            <div className="md:col-span-3 p-8 md:p-12 bg-white">
              <div className="grid sm:grid-cols-2 gap-4">
                {activeData.items.map((item, index) => (
                  <div
                    key={index}
                    className="group p-4 rounded-xl hover:bg-slate-50 transition-colors duration-300"
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-8 h-8 rounded-lg ${colors.bg} flex items-center justify-center flex-shrink-0`}>
                        <Check className={`w-4 h-4 ${colors.text}`} />
                      </div>
                      <div>
                        <h4 className="font-semibold text-slate-800 mb-1">
                          {item.name}
                        </h4>
                        <p className="text-sm text-slate-500">
                          {item.description}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;
